import { ChangeDetectorRef, Component, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { externalLinks } from 'src/app/config';

import { CommonModule, DatePipe } from '@angular/common';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter, Subscription } from 'rxjs';
import { AppSearchDialogComponent } from 'src/app/components/search-dialog/search-dialog.component';
import { SharedModule } from 'src/app/components/shared.module';
import { TranscriptionService } from 'src/app/services/apps/audio/transcription.service';
import { AuthService } from 'src/app/services/apps/auth/auth.service';
import { CompanyService } from 'src/app/services/apps/company/company.service';
import { DemandService } from 'src/app/services/apps/demand/demand.service';
import { NotificationService } from 'src/app/services/apps/notification/notification.service';
import { feedback_types_enum } from 'src/assets/types/enums/feedback-types.enum';
import { Notification } from 'src/assets/types/interface/notification.interface';
import { Tenant } from 'src/assets/types/interface/tenant.interface';
import { User } from 'src/assets/types/interface/user.interface';
import { AppBreadcrumbComponent } from '../../shared/breadcrumb/breadcrumb.component';
import { user_panel } from './data';
import { UserPanel } from 'src/assets/types/interface/user-panel.interface';
import { environment } from 'src/environments/environment';

import { AddDemandAiModalComponent } from 'src/app/pages/modules/management/demand/request/modals/ai/ai-demand.component';
import { NotificationReadEvent } from 'src/assets/types/interface/notification-read-event.interface';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    RouterModule,
    TablerIconsModule,
    MaterialModule,
    SharedModule,
    DatePipe,
    CommonModule,
    AppBreadcrumbComponent,
  ],
  templateUrl: './header.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent {
  readonly helpCenterUrl = externalLinks.helpCenter;
  readonly appVersion = environment.APP_VERSION;
  readonly appEnvironment = environment.name;
  showBreadcrumb = true;

  notifications: Notification[] = [];
  unreadNotifications = 0;

  page = 1;
  pageSize = 5;
  loading = false;
  allLoaded = false;
  userPanel = user_panel;

  onlyUnreaded = false;

  user: User;
  tenantType: string;

  tenantOptions: string[] = ['Tomador', 'Prestador'];
  selectedTenant: string;

  showCancel = true;
  showConfirm = true;
  showAlert = false;
  alertMessage = '';

  feedbackMessage = '';
  feedbackType = feedback_types_enum.error;
  showFeedback = false;

  private alertResolve: ((value: boolean) => void) | null;
  private socketSub: Subscription;
  private notificationReadSub: Subscription;

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private companyService: CompanyService,
    private cdr: ChangeDetectorRef,
    private notificationService: NotificationService,
    private snackBar: MatSnackBar,
    private demandService: DemandService,
    private transcriptionService: TranscriptionService,
  ) {}

  ngOnInit() {
    this.user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const tenant = JSON.parse(localStorage.getItem('tenant') ?? '{}');
    this.tenantType = tenant.type;
    this.selectedTenant = tenant.type;

    if (this.notificationService.hasAccessToken()) {
      this.loadNotifications(true);
      this.notificationService.connect();
      this.socketSub = this.notificationService
        .listenToNotifications()
        .subscribe((event) => {
          this.notifications = [
            event.notification,
            ...this.notifications.filter(
              (notification) => notification.uuid !== event.notification.uuid,
            ),
          ].slice(0, this.pageSize);
          this.unreadNotifications = event.totalUnread;
          this.page = 2;
          this.allLoaded = false;
          this.cdr.detectChanges();
        });
      this.notificationReadSub = this.notificationService
        .listenToNotificationReads()
        .subscribe((event) => this.applyNotificationRead(event));
    }

    this.updateRouteLayoutState();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateRouteLayoutState();
      });
  }

  ngOnDestroy() {
    if (this.socketSub) {
      this.socketSub.unsubscribe();
    }
    if (this.notificationReadSub) {
      this.notificationReadSub.unsubscribe();
    }
    this.notificationService.disconnect();
  }

  private updateRouteLayoutState() {
    let route = this.activatedRoute;

    while (route.firstChild) {
      route = route.firstChild;
    }

    this.showBreadcrumb = route.snapshot.data['hideBreadcrumb'] !== true;
  }

  getTenants() {
    return JSON.parse(localStorage.getItem('tenant') ?? '{}');
  }

  clickNotification(notification: Notification): void {
    if (!notification.readed) {
      this.notificationService.markedAsRead(notification.uuid).subscribe({
        next: (event) => this.applyNotificationRead(event),
      });
    }
    if (notification.link) {
      if (
        this.router.isActive(notification.link, {
          paths: 'exact',
          queryParams: 'exact',
          fragment: 'ignored',
          matrixParams: 'exact',
        })
      ) {
        this.notificationService.requestActiveRouteRefresh();
      } else {
        this.router.navigateByUrl(notification.link);
      }
    }
    this.cdr.detectChanges();
  }

  private applyNotificationRead(event: NotificationReadEvent): void {
    this.unreadNotifications = event.totalUnread;
    const notification = this.notifications.find(
      (item) => item.uuid === event.notificationUuid,
    );

    if (notification) {
      if (this.onlyUnreaded) {
        this.notifications = this.notifications.filter(
          (item) => item.uuid !== event.notificationUuid,
        );
      } else {
        notification.readed = true;
      }
    }

    this.cdr.detectChanges();
  }

  loadNotifications(reset: boolean = false) {
    if (!this.notificationService.hasAccessToken() || this.loading) return;

    if (reset) {
      this.page = 1;
      this.allLoaded = false;
      this.notifications = [];
    }

    if (this.allLoaded && !reset) return;

    this.loading = true;
    const currentFilter = this.onlyUnreaded ? 'unreaded' : 'all';
    this.notificationService
      .getNotificationsWithPagination(this.pageSize, this.page, currentFilter)
      .subscribe({
        next: (res) => {
          const newNotifications = res.data;
          this.unreadNotifications = res.totalUnread;
          if (newNotifications.length < this.pageSize) {
            this.allLoaded = true;
          }
          if (reset) {
            this.notifications = newNotifications;
          } else {
            this.notifications = [...this.notifications, ...newNotifications];
          }
          this.page++;
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  onToggleChange(event: MatSlideToggleChange) {
    this.onlyUnreaded = event.checked;
    this.loadNotifications(true);
  }

  onScroll(event: any) {
    const element = event.target;
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 20) {
      this.loadNotifications(false);
    }
  }

  menuOpened() {
    this.loadNotifications(true);
  }

  onTenantChange(tenantType: string) {
    if (tenantType && this.selectedTenant === tenantType) return;

    this.selectedTenant = tenantType;
    this.tenantType = tenantType;

    const tenants = JSON.parse(localStorage.getItem('tenants') ?? '{}');

    let selectedTenant = tenants.find(
      (tenant: Tenant) => tenant.type === tenantType,
    );

    const general_access_token = localStorage.getItem('general_access_token');
    this.authService
      .chooseTenant(selectedTenant.uuid, general_access_token)
      .subscribe({
        next: (data) => {
          const tenant = data.tenant;

          localStorage.setItem('tenant', JSON.stringify(tenant));

          localStorage.setItem('user', JSON.stringify(data.user));
          // localStorage.removeItem('tenants');
          localStorage.setItem('access_token', data.access_token);

          this.companyService
            .getCompanyByAccessToken(data.access_token)
            .subscribe({
              next: (company) => {
                if (![null, undefined, '', {}, []].includes(company)) {
                  localStorage.setItem('company', JSON.stringify(company));
                  this.cdr.detectChanges();
                  this.router.navigateByUrl('/home').then(() => {
                    window.location.reload();
                  });
                } else {
                  this.router.navigate([
                    '/authentication/boxed-register-company',
                  ]);
                }
              },
              error: () => {
                this.alertMessage = 'Erro ao buscar empresa. Tente novamente.';
                this.showAlert = true;
                setTimeout(() => {
                  this.showAlert = false;
                }, 3000);
              },
            });
        },
        error: () => {},
      });
  }

  async createNewTenant() {
    const confirmed = await this.showConfirmationAlert(
      'Deseja mesmo ingressar nesse novo plano?',
    );

    if (!confirmed) return;

    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const tenant = JSON.parse(localStorage.getItem('tenant') ?? '{}');
    const access_token = localStorage.getItem('access_token') ?? '';

    const data = {
      name: user.name,
      company: tenant.name,
      email: user.email,
      typeTenant: tenant.type === 'Tomador' ? 'Prestador' : 'Tomador',
    };

    this.authService.signUp(data).subscribe({
      next: (response) => {
        const newTenant = response.body;
        if (newTenant) {
          this.authService
            .chooseTenant(newTenant.uuid, access_token)
            .subscribe({
              next: (data) => {
                const tenant = data.tenant;

                localStorage.setItem('tenant', JSON.stringify(tenant));
                localStorage.setItem('user', JSON.stringify(data.user));
                localStorage.setItem('access_token', data.access_token);

                this.router.navigate([
                  '/authentication/boxed-register-company',
                ]);
              },
              error: (error) => {
                if (error.status === 400) {
                  this.alertMessage =
                    'Erro ao acessar novo contexto. Tente novamente.';
                  this.showAlert = true;
                  setTimeout(() => {
                    this.showAlert = false;
                  }, 3000);
                }
              },
            });
        }
      },
      error: (error) => {
        if (error.status === 400) {
          this.alertMessage = 'Erro ao cadastrar usuário. Tente novamente.';
          this.showAlert = true;
          setTimeout(() => {
            this.showAlert = false;
          }, 3000);
        }
      },
    });
  }

  async showConfirmationAlert(message: string): Promise<boolean> {
    this.alertMessage = message;
    this.showAlert = true;
    this.showCancel = this.showConfirm = true;

    return new Promise<boolean>((resolve) => {
      this.alertResolve = resolve;
    });
  }

  onAlertClosed(confirmed: boolean) {
    this.showAlert = false;
    if (this.alertResolve) {
      this.alertResolve(confirmed);
      this.alertResolve = null;
    }
  }

  async openDialog() {
    this.demandService.ruleIsAlreadyConfigured().subscribe(async (res) => {
      let confirmed;
      if (res.canOpen) {
        const dialogRef = this.dialog.open(AddDemandAiModalComponent, {
          width: 'min(1120px, calc(100vw - 48px))',
          maxWidth: 'calc(100vw - 24px)',
          maxHeight: 'calc(100vh - 24px)',
          autoFocus: false,
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result?.statusCode === 404) {
            setTimeout(() => {
              this.showFeedback = false;
            }, 5000);
            this.feedbackMessage = result.message;
            this.showFeedback = true;
          }
          this.transcriptionService.cancelTranscription();
        });
      } else {
        confirmed = await this.showConfirmationAlert(res.message);
        if (confirmed) {
          this.showAlert = false;
          this.router.navigate(['configuracoes/regras']);
        }
      }
    });
  }

  oldOpenDialog() {
    const dialogRef = this.dialog.open(AppSearchDialogComponent);
  }

  openNotifications() {
    this.router.navigate(['/plataforma/notificacoes']);
  }

  selected(profile: any) {
    if (profile.title === 'Sair') {
      localStorage.clear();
    }
    this.router.navigate([profile.link]);
  }

  get unreadNotificationsLabel(): string {
    const n = this.unreadNotifications;
    if (n === 0) return 'Nenhuma notificação não aberta';
    if (n === 1) return '1 notificação não aberta';
    return `${n} notificações não abertas`;
  }

  get unreadNotificationsBadge(): string {
    return this.unreadNotifications >= 1000
      ? '+999'
      : String(this.unreadNotifications);
  }

  slideToggleNotificationMessage() {
    return !this.onlyUnreaded ? 'Exibir apenas não lidas' : 'Exibir todas';
  }

  isVisible(profile: UserPanel) {
    return profile.context.includes(this.tenantType);
  }
}
